<template>
  <main class="login-page login-shell">
    <div class="login-stage">
      <section class="login-visual" aria-labelledby="login-brand-title">
        <header class="login-brand">
          <h1 id="login-brand-title">社区管理虚拟仿真沙盘</h1>
          <p>智慧城市网络化管理平台 · 公共政策智能分析决策平台</p>
        </header>
        <img class="login-model" src="../assets/login/community-model.png" alt="社区建筑与居民服务虚拟仿真模型" draggable="false">
        <ul class="login-capabilities" aria-label="平台功能">
          <li v-for="item in capabilities" :key="item.key" class="login-badge" :class="'login-badge-' + item.key">
            <img :src="item.icon" alt="" aria-hidden="true"><span>{{ item.name }}</span>
          </li>
        </ul>
      </section>
      <section class="login-panel login-form-panel" aria-labelledby="login-title">
        <h2 id="login-title" class="login-title">欢迎登录</h2>
        <form class="login-form" @submit.prevent="login">
          <operation-message :value="error" type="error" @consumed="error = ''" />
          <p v-if="$route.query.passwordChanged === '1'" class="notice success" role="status">密码修改成功，请使用新密码登录。</p>
          <div class="login-field">
            <label for="login-account">登录账号</label>
            <div class="login-input-wrap">
              <img src="../assets/login/account.png" alt="" aria-hidden="true">
              <input id="login-account" ref="account" v-model.trim="form.account" name="account" autocomplete="username" placeholder="请输入登录账号" :disabled="loading" required>
            </div>
          </div>
          <div class="login-field">
            <label for="login-password">登录密码</label><small class="login-account-help">账号由授课教师或管理员发放；忘记密码请联系账号发放单位。</small>
            <div class="login-input-wrap">
              <img src="../assets/login/password.png" alt="" aria-hidden="true">
              <input id="login-password" ref="password" v-model="form.password" name="password" type="password" autocomplete="current-password" placeholder="请输入登录密码" :disabled="loading" required>
            </div>
          </div>
          <button class="login-submit primary-login" type="submit" :disabled="loading" :aria-busy="loading"><span>{{ loading ? '登录中…' : '登录' }}</span></button>
        </form>
        <p class="login-terms">登录前请阅读 <button type="button" @click="openPolicy('agreement', $event)">《用户协议》</button><button type="button" @click="openPolicy('privacy', $event)">《隐私政策》</button></p>
      </section>
    </div>
    <lab-dialog class="login-policy-dialog" :visible="!!policy" :title="policy === 'agreement' ? '用户协议' : '隐私政策'" size="wide" @close="closePolicy" @keydown.native="onPolicyKeydown">
      <login-legal-document v-if="policy" :key="policy" :document-key="policy" />
      <p v-if="policyError" class="login-policy-download-error" role="alert">{{ policyError }}</p>
      <template #footer><button class="btn small login-policy-switch" type="button" @click="switchPolicy">查看{{ policy === 'agreement' ? '隐私政策' : '用户协议' }}</button><button class="btn small" type="button" @click="downloadPolicy">下载正文</button><button class="btn primary small" type="button" @click="closePolicy">关闭</button></template>
    </lab-dialog>
  </main>
</template>

<script>
import { errorText } from '../services/api'
import LabDialog from '../components/NetworkLabDialog.vue'
import LoginLegalDocument from '../components/LoginLegalDocument.vue'
import legalContent from '../content/legal-documents.json'
import { legalDocumentText } from '../content/legalText'
import '../assets/login-legal.css'
import collection from '../assets/login/collection.png'
import simulation from '../assets/login/simulation.png'
import trace from '../assets/login/trace.png'
import report from '../assets/login/report.png'

export default {
  name: 'Login',
  components: { LabDialog, LoginLegalDocument },
  data () {
    return {
      loading: false, error: '', policy: '', policyTrigger: null, policyError: '', policyDownloads: [],
      form: { account: '', password: '' },
      capabilities: [
        { key: 'collection', name: '网络采集', icon: collection },
        { key: 'simulation', name: '虚拟仿真', icon: simulation },
        { key: 'trace', name: '全程回溯', icon: trace },
        { key: 'report', name: '评估报告', icon: report }
      ]
    }
  },
  mounted () { document.body.classList.add('login-screen-active') },
  beforeDestroy () { document.body.classList.remove('login-screen-active', 'login-policy-open'); this.policyDownloads.forEach(url => URL.revokeObjectURL(url)) },
  methods: {
    openPolicy (policy, event) { if (!legalContent.documents[policy]) return; this.policyTrigger = event.currentTarget; this.policyError = ''; this.policy = policy; document.body.classList.add('login-policy-open') },
    switchPolicy () { this.policyError = ''; this.policy = this.policy === 'agreement' ? 'privacy' : 'agreement' },
    closePolicy () { this.policy = ''; this.policyError = ''; document.body.classList.remove('login-policy-open'); this.$nextTick(() => { if (this.policyTrigger && this.policyTrigger.isConnected) this.policyTrigger.focus() }) },
    onPolicyKeydown (event) {
      if (event.key !== 'Tab' || !this.policy) return
      const dialog = this.$el.querySelector('.login-policy-dialog [role="dialog"]')
      const controls = dialog && [...dialog.querySelectorAll('button:not(:disabled),a[href],summary,[tabindex="0"]')].filter(element => element.getClientRects().length)
      if (!controls || !controls.length) return
      const first = controls[0]; const last = controls[controls.length - 1]; const active = document.activeElement
      if (active === dialog || !dialog.contains(active)) { event.preventDefault(); (event.shiftKey ? last : first).focus() } else if (event.shiftKey && active === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus() }
    },
    downloadPolicy () {
      if (!legalContent.documents[this.policy]) return
      this.policyError = ''
      try {
        const blob = new Blob(['\uFEFF', legalDocumentText(legalContent, this.policy)], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob); this.policyDownloads.push(url)
        const link = document.createElement('a'); link.href = url; link.download = legalContent.platformName + '-' + legalContent.documents[this.policy].label + '-v' + legalContent.version + '.txt'
        document.body.appendChild(link); link.click(); link.remove()
        window.setTimeout(() => { URL.revokeObjectURL(url); this.policyDownloads = this.policyDownloads.filter(item => item !== url) }, 1000)
      } catch (error) { this.policyError = '正文下载未成功，请重试或联系平台服务人员。'; this.$message.error(this.policyError) }
    },
    async login () {
      if (this.loading) return
      const account = this.form.account.trim()
      if (!account || !this.form.password) {
        this.error = '请输入登录账号和密码'
        this.$refs[account ? 'password' : 'account'].focus()
        return
      }
      this.error = ''
      this.loading = true
      try {
        await this.$store.dispatch('login', { account, password: this.form.password })
        await this.$router.replace('/')
      } catch (error) {
        this.error = errorText(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
